import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Home, Search, ChevronLeft, Terminal } from 'lucide-react';

export default function NotFound() {
  return (
    <div className="min-h-[85vh] flex flex-col items-center justify-center p-6 text-center overflow-hidden">
      <motion.div 
        initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ type: "spring", damping: 12 }}
        className="relative mb-8"
      >
        <div className="text-[120px] md:text-[200px] font-black text-slate-100 dark:text-slate-800/40 leading-none select-none tracking-tighter">404</div>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div 
            animate={{ 
              y: [0, -20, 0],
              rotate: [0, 10, -10, 0]
            }}
            transition={{ 
              repeat: Infinity, 
              duration: 5, 
              ease: "easeInOut" 
            }}
            className="w-24 h-24 md:w-32 md:h-32 bg-accent rounded-[2.5rem] shadow-2xl shadow-accent/40 flex items-center justify-center text-white"
          >
            <Search className="w-12 h-12 md:w-16 md:h-16" />
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="max-w-lg"
      >
        <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-4 dark:text-white font-sans italic">
          Whoops! You're off the grid.
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mb-10 text-sm md:text-base leading-relaxed font-semibold">
          Even our high-performance AI couldn't find this page. It's either been deleted by a rogue script, 
          was never here to begin with, or is currently traveling through a wormhole.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link 
            to="/" 
            className="w-full sm:w-auto px-10 py-4 bg-accent text-white rounded-2xl font-black text-sm flex items-center justify-center gap-3 hover:scale-105 active:scale-95 transition-all shadow-xl shadow-accent/20"
          >
            <Home className="w-4 h-4" /> Return to Orbit
          </Link>
          <button 
            onClick={() => window.history.back()}
            className="w-full sm:w-auto px-10 py-4 bg-white dark:bg-slate-900 border-2 border-line dark:border-slate-800 rounded-2xl font-black text-sm flex items-center justify-center gap-3 hover:border-accent transition-all dark:text-white"
          >
            <ChevronLeft className="w-4 h-4" /> Go Back
          </button>
        </div>

        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-16 flex items-center justify-center gap-3 text-[10px] font-black uppercase tracking-[0.4em] text-slate-300 dark:text-slate-700"
        >
           <Terminal className="w-3 h-3" />
           404 ERROR: System is working exactly as (un)expected
        </motion.div>
      </motion.div>

      {/* Decorative background elements */}
      <div className="absolute top-1/4 -left-20 w-64 h-64 bg-accent/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
    </div>
  );
}
